import {
  AppPageHeader,
  ColorSwatch,
  ColumnsType,
  Group,
  InfiniteTable,
} from '@/components/_common';
import ActionIcon from '@/components/_common/ActionIcon';
import BankConciliationPopoverDetails from '@/components/_common/BankConciliation/BankConciliationPopoverDetails';
import StatusTag from '@/components/_common/StatusTag';
import BillToReceiveExtraFilter from '@/components/Registry/BillToReceive/BillToReceiveExtraFilter';
import AccountReceivableAdvancedModalFilters from '@/components/Registry/BillToReceive/InstallmentsAdvancedModalFilters';
import { useAccountReceivableList } from '@/core/domain/financial/AccountReceivable/AccountReceivable.hooks';
import {
  AccountReceivable,
  AccountReceivableListRequest,
  AccountReceivableOrderColumn,
  AccountReceivableSituation,
  AccountReceivableSituationColor,
  AccountRecivableSituationType,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import { useFinancialConsultationReceivableBind } from '@/core/domain/financial/Consultation/consultation.hooks';
import { useDebounce, useDisclosure } from '@/hooks';
import { quantityFormatter } from '@/utils/number';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { COLUMNS_WIDTH } from '@/utils/table';
import { Popover, Modal, Button, Tooltip } from 'antd';
import moment from 'moment';
import { useCallback, useState } from 'react';
import { Check, Link } from 'react-feather';
import { useIntl } from 'react-intl';

interface Props {
  migrationId: number;
}

export default function BillToReceiveMigrationConsultBind(props: Props) {
  const intl = useIntl();

  const [opened, action] = useDisclosure();

  const [queryParams, setQueryParams] = useState<AccountReceivableListRequest>({
    order: 'DESC',
    orderColumns: AccountReceivableOrderColumn.code,
  });

  const [advancedFiltersOpened, advancedFiltersAction] = useDisclosure();

  function setParams(params: AccountReceivableListRequest, reset?: boolean) {
    if (!reset) {
      setQueryParams((state) => ({ ...state, ...params }));
    } else {
      setQueryParams(params);
    }
  }

  const queryDebounce = useDebounce(queryParams, 500);

  const installmentList = useAccountReceivableList(queryDebounce, opened);
  const consultPayableBind = useFinancialConsultationReceivableBind();

  const isLoading = consultPayableBind.isLoading;

  const installments = flatInfiniteData(installmentList.data);

  const handleSubmit = async (installment: AccountReceivable) => {
    try {
      await consultPayableBind.mutateAsync({
        consultationId: props.migrationId,
        mainInstallmentReceivableId: installment.id,
        mainReceivableId: installment.accountReceivableId,
      });
      action.close();
    } catch {}
  };

  const handleClose = useCallback(() => {
    if (isLoading) return;
    action.close();
  }, [isLoading]);

  const columns: ColumnsType<AccountReceivable> = [
    {
      width: 140,
      dataIndex: 'code',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.code',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.code,
      render: (_, row) => (
        <Group gap={10}>
          <>{row.code}</>

          {!!row.conciliateInstallmentResponse && (
            <BankConciliationPopoverDetails
              conciliatedInstallment={row.conciliateInstallmentResponse}
            />
          )}
        </Group>
      ),
    },
    {
      dataIndex: 'installmentTag',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.installmentTag',
      }),
      width: COLUMNS_WIDTH.price,
      key: AccountReceivableOrderColumn.installmentTag,
      align: 'center',
      render: (_, row) =>
        !!row.installmentTag && (
          <Group position="center">
            <Tooltip
              title={row.installmentTag.name.toUpperCase()}
              color={row.installmentTag.color}
              placement="right"
            >
              <div>
                <ColorSwatch backgroundColor={row.installmentTag.color} />
              </div>
            </Tooltip>
          </Group>
        ),
    },
    {
      width: 220,
      dataIndex: 'companyName',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.companies',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.company,
    },
    {
      dataIndex: 'documentNumber',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.documentNumber',
      }),
      width: 220,
      key: AccountReceivableOrderColumn.documentNumber,
      sorter: true,
      ellipsis: true,
    },
    {
      width: 220,
      dataIndex: 'debtor',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.debtor',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.debtor,
    },
    {
      width: COLUMNS_WIDTH.action2,
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.description',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.description,
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'documentType',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.documentType',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.documentType,
    },
    {
      width: 100,
      dataIndex: 'emissionDate',
      title: 'Dt. emissão',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      sorter: true,
      key: AccountReceivableOrderColumn.emissionDate,
    },
    {
      width: 100,
      dataIndex: 'dueDate',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.dueDate',
      }),
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      sorter: true,
      key: AccountReceivableOrderColumn.dueDate,
    },
    {
      width: 100,
      dataIndex: 'payDate',
      title: 'Dt. pagamento',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      sorter: true,
      key: AccountReceivableOrderColumn.payDate,
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'authorizationNumber',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.authorizationNumber',
      }),
      key: AccountReceivableOrderColumn.authorizationNumber,
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'situation',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.situation',
      }),
      key: AccountReceivableOrderColumn.situation,
      render: (situation: AccountReceivableSituation) => (
        <StatusTag
          color={AccountReceivableSituationColor[situation]}
          label={AccountRecivableSituationType[situation]}
        />
      ),
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'installmentValue',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.value',
      }),
      sorter: true,
      align: 'right',
      key: AccountReceivableOrderColumn.value,
      render: (_, item) =>
        `R$ ${quantityFormatter(
          item.installmentValue + (item.discountIncrease ?? 0),
        )}`,
    },
  ];

  return (
    <>
      <Popover content="Vincular">
        <ActionIcon onClick={action.open}>
          <Link size={18} />
        </ActionIcon>
      </Popover>

      <Modal
        open={opened}
        onCancel={handleClose}
        title="Vincular contas a receber cadastrados"
        width="100%"
        footer={false}
        closable={!isLoading}
        maskClosable={!isLoading}
      >
        <p>Selecione uma parcela para vincular a consulta de migração.</p>

        <AppPageHeader
          onSearch={(value) => setParams({ search: value })}
          searchValue={queryParams.search}
          headerActions={({
            isShowingFilters,
          }: {
            isShowingFilters: boolean;
          }) =>
            isShowingFilters && (
              <Button
                type="primary"
                onClick={() =>
                  setParams(
                    {
                      order: 'DESC',
                      orderColumns: AccountReceivableOrderColumn.code,
                    },
                    true,
                  )
                }
              >
                {intl.formatMessage({ id: 'commons.clear.filters' })}
              </Button>
            )
          }
          extraFilters={
            <BillToReceiveExtraFilter
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
          handleClickAdvancedFilters={advancedFiltersAction.open}
        />

        <InfiniteTable
          dataSource={installments}
          columns={[
            ...columns,
            {
              title: 'Selecionar',
              fixed: 'right',
              align: 'center',
              width: 80,
              render: (_, item) => (
                <Button
                  icon={<Check />}
                  shape="circle"
                  loading={
                    consultPayableBind.isLoading &&
                    consultPayableBind.variables
                      ?.mainInstallmentReceivableId === item.id
                  }
                  onClick={() => handleSubmit(item)}
                />
              ),
            },
          ]}
          fetchNextPage={installmentList.fetchNextPage}
          hasNextPage={installmentList.hasNextPage}
          isFetching={installmentList.isFetching}
          loading={installmentList.isLoading}
          rowKey={(item) => item.id}
          scroll={{ x: true }}
        />

        <AccountReceivableAdvancedModalFilters
          open={advancedFiltersOpened}
          onCancel={advancedFiltersAction.close}
          handleSetFilter={(values) =>
            setQueryParams((prev) => ({ ...prev, ...values }))
          }
          queryParams={queryParams}
        />
      </Modal>
    </>
  );
}
